import { useState, useEffect } from 'react';
import { useSocket } from '../contexts/SocketContext';

/**
 * Custom hook to track user's geolocation and automatically join safety zones
 * @param {Object} options - Configuration options
 * @param {boolean} options.enabled - Whether to track location (default: true)
 * @param {number} options.radius - Alert radius in meters (default: 5000)
 * @param {number} options.updateInterval - How often to update location in ms (default: 60000)
 * @returns {Object} Location data and methods
 */
export const useGeolocation = (options = {}) => {
  const {
    enabled = true,
    radius = 5000,
    updateInterval = 60000 // Update every minute
  } = options;

  const [location, setLocation] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [permission, setPermission] = useState('prompt');

  const { joinSafetyZone, leaveSafetyZone, updateLocation, isConnected } = useSocket();

  // Request geolocation permission and get initial location
  useEffect(() => {
    if (!enabled || !navigator.geolocation) {
      setLoading(false);
      setError('Geolocation is not supported by your browser');
      return;
    }

    // Check permission status if available
    if (navigator.permissions) {
      navigator.permissions.query({ name: 'geolocation' })
        .then((result) => {
          setPermission(result.state);
          result.onchange = () => {
            setPermission(result.state);
          };
        })
        .catch((err) => {
          console.warn('Permission API not available:', err);
        });
    }

    const successHandler = (position) => {
      const newLocation = {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        accuracy: position.coords.accuracy,
        timestamp: position.timestamp
      };

      setLocation(newLocation);
      setLoading(false);
      setError(null);

      // Join safety zone if socket is connected
      if (isConnected) {
        joinSafetyZone(newLocation.latitude, newLocation.longitude, radius);
      }

      console.log('📍 Location updated:', newLocation);
    };

    const errorHandler = (err) => {
      setLoading(false);
      let errorMessage = 'Unable to retrieve your location';

      switch (err.code) {
        case err.PERMISSION_DENIED:
          errorMessage = 'Location permission denied';
          setPermission('denied');
          break;
        case err.POSITION_UNAVAILABLE:
          errorMessage = 'Location information unavailable';
          break;
        case err.TIMEOUT:
          errorMessage = 'Location request timed out';
          break;
        default:
          errorMessage = 'An unknown error occurred';
      }

      setError(errorMessage);
      console.error('Geolocation error:', errorMessage, err);
    };

    // Get initial position
    navigator.geolocation.getCurrentPosition(successHandler, errorHandler, {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 0
    });

    // Watch position for updates
    let watchId;
    if (updateInterval > 0) {
      watchId = navigator.geolocation.watchPosition(
        (position) => {
          const newLocation = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
            timestamp: position.timestamp
          };

          // Only update if location has changed significantly (> 50 meters)
          if (!location ||
              calculateDistance(
                location.latitude,
                location.longitude,
                newLocation.latitude,
                newLocation.longitude
              ) > 50) {
            setLocation(newLocation);

            // Update socket location if connected
            if (isConnected) {
              updateLocation(newLocation.latitude, newLocation.longitude, radius);
            }

            console.log('📍 Location moved:', newLocation);
          }
        },
        errorHandler,
        {
          enableHighAccuracy: false, // Less battery drain for watch
          timeout: 10000,
          maximumAge: updateInterval
        }
      );
    }

    // Cleanup
    return () => {
      if (watchId) {
        navigator.geolocation.clearWatch(watchId);
      }
      leaveSafetyZone();
    };
  }, [enabled, radius, updateInterval, isConnected]);

  // Rejoin safety zone when socket reconnects
  useEffect(() => {
    if (isConnected && location && enabled) {
      joinSafetyZone(location.latitude, location.longitude, radius);
    }
  }, [isConnected]);

  /**
   * Manually request location update
   */
  const refreshLocation = () => {
    if (!navigator.geolocation) {
      setError('Geolocation is not supported');
      return;
    }

    setLoading(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const newLocation = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
          timestamp: position.timestamp
        };

        setLocation(newLocation);
        setLoading(false);
        setError(null);

        if (isConnected) {
          updateLocation(newLocation.latitude, newLocation.longitude, radius);
        }
      },
      (err) => {
        setLoading(false);
        setError('Failed to refresh location');
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  };

  /**
   * Request location permission explicitly
   */
  const requestPermission = async () => {
    if (!navigator.geolocation) {
      return 'unsupported';
    }

    try {
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject);
      });

      setPermission('granted');
      return 'granted';
    } catch (err) {
      setPermission('denied');
      return 'denied';
    }
  };

  return {
    location,
    error,
    loading,
    permission,
    refreshLocation,
    requestPermission,
    isTracking: enabled && !error && location !== null
  };
};

/**
 * Calculate distance between two coordinates using Haversine formula
 */
function calculateDistance(lat1, lon1, lat2, lon2) {
  const R = 6371e3; // Earth's radius in meters
  const φ1 = lat1 * Math.PI / 180;
  const φ2 = lat2 * Math.PI / 180;
  const Δφ = (lat2 - lat1) * Math.PI / 180;
  const Δλ = (lon2 - lon1) * Math.PI / 180;

  const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
            Math.cos(φ1) * Math.cos(φ2) *
            Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c; // Distance in meters
}

export default useGeolocation;
